import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Search as SearchIcon,
    Clear as ClearIcon,
    Close,
} from '@mui/icons-material';
import { orderAPI } from '../api';

// ── Helpers ────────────────────────────────────────────────────────────────

const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const STATUS_META = {
    created:   { label: 'Created',   color: 'default' },
    accepted:  { label: 'Accepted',  color: 'primary' },
    delivered: { label: 'Delivered', color: 'success' },
    cancelled: { label: 'Cancelled', color: 'error' },
};

const PAYMENT_META = {
    void:          { label: '—',        color: 'default' },
    due:           { label: 'Due',      color: 'warning' },
    paid_directly: { label: 'Paid',     color: 'success' },
    invoice:       { label: 'Bill',     color: 'info' },
    pay_later:     { label: 'PayLater', color: 'secondary' },
};

const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Return YYYY-MM-DD for a Date object
const toISODate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Monday of the week containing d
const weekMonday = (d) => {
    const clone = new Date(d);
    const day = clone.getDay(); // 0=Sun
    const diff = (day === 0) ? -6 : 1 - day;
    clone.setDate(clone.getDate() + diff);
    return clone;
};

// Sunday of the week containing d
const weekSunday = (d) => {
    const mon = weekMonday(d);
    const sun = new Date(mon);
    sun.setDate(sun.getDate() + 6);
    return sun;
};

const formatWeekLabel = (mondayDate) => {
    const sun = weekSunday(mondayDate);
    if (mondayDate.getMonth() === sun.getMonth()) {
        return `${mondayDate.getDate()}–${sun.getDate()} ${MONTH_SHORT[mondayDate.getMonth()]} ${mondayDate.getFullYear()}`;
    }
    return `${mondayDate.getDate()} ${MONTH_SHORT[mondayDate.getMonth()]}–${sun.getDate()} ${MONTH_SHORT[sun.getMonth()]} ${mondayDate.getFullYear()}`;
};

// Derive date_from / date_to strings from mode + selection
const deriveDateRange = (mode, selectedDay, selectedWeekMonday, selectedMonthState) => {
    if (mode === 'day') {
        return { date_from: selectedDay, date_to: selectedDay };
    }
    if (mode === 'week') {
        const mon = selectedWeekMonday;
        const sun = weekSunday(selectedWeekMonday);
        return { date_from: toISODate(mon), date_to: toISODate(sun) };
    }
    // month
    const { year, month } = selectedMonthState;
    const last = new Date(year, month, 0).getDate(); // day 0 of next month = last day of this month
    return {
        date_from: `${year}-${String(month).padStart(2, '0')}-01`,
        date_to: `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}`,
    };
};

// Is the current selection already at today / this week / this month?
const isAtLatest = (mode, selectedDay, selectedWeekMonday, selectedMonth) => {
    const today = new Date();
    if (mode === 'day') return selectedDay === toISODate(today);
    if (mode === 'week') return toISODate(weekMonday(today)) === toISODate(selectedWeekMonday);
    const now = { year: today.getFullYear(), month: today.getMonth() + 1 };
    return selectedMonth.year === now.year && selectedMonth.month === now.month;
};

const PAGE_SIZE = 50;

// ── Component ──────────────────────────────────────────────────────────────

export default function OrdersSummaryPage({ businessId }) {
    const today = new Date();

    // Period mode
    const [periodMode, setPeriodMode] = useState('month');
    const [selectedDay, setSelectedDay] = useState(toISODate(today));
    const [selectedWeekMonday, setSelectedWeekMonday] = useState(weekMonday(today));
    const [selectedMonth, setSelectedMonth] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');
    const [agentFilter, setAgentFilter] = useState('');   // agent_user_id or ''
    const [areaFilter, setAreaFilter] = useState('');     // area_id or ''
    const [sortBy, setSortBy] = useState('inserted_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Data
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);

    // Detail dialog
    const [detailOrder, setDetailOrder] = useState(null);

    // Debounce ref for search
    const searchTimer = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(searchTimer.current);
    }, [search]);

    // Derived date range
    const { date_from, date_to } = useMemo(
        () => deriveDateRange(periodMode, selectedDay, selectedWeekMonday, selectedMonth),
        [periodMode, selectedDay, selectedWeekMonday, selectedMonth]
    );

    // ── Load summary stats ──────────────────────────────────────────────────
    const loadSummary = useCallback(async () => {
        if (!businessId) return;
        setSummaryLoading(true);
        try {
            const params = { date_from, date_to };
            if (areaFilter !== '') params.area_id = areaFilter;
            if (agentFilter !== '') params.agent_id = agentFilter;
            const res = await orderAPI.getOrdersSummary(businessId, params);
            setSummary(res.data);
        } catch (e) {
            console.error('Failed to load orders summary', e);
        } finally {
            setSummaryLoading(false);
        }
    }, [businessId, date_from, date_to, areaFilter, agentFilter]);

    // ── Load order list ─────────────────────────────────────────────────────
    const loadOrders = useCallback(async (pageNum = 0, append = false) => {
        if (!businessId) return;
        setOrdersLoading(true);
        try {
            const params = {
                date_from,
                date_to,
                limit: PAGE_SIZE,
                offset: pageNum * PAGE_SIZE,
                sort_by: sortBy,
                sort_order: sortOrder,
            };
            if (statusFilter) params.status = statusFilter;
            if (paymentFilter) params.payment_status = paymentFilter;
            if (agentFilter !== '') params.agent_id = agentFilter;
            if (areaFilter !== '') params.area_id = areaFilter;
            if (debouncedSearch) params.search = debouncedSearch;

            const res = await orderAPI.listOrders(businessId, params);
            const data = res.data || [];
            setOrders(prev => append ? [...prev, ...data] : data);
            setHasMore(data.length === PAGE_SIZE);
        } catch (e) {
            console.error('Failed to load orders', e);
        } finally {
            setOrdersLoading(false);
        }
    }, [businessId, date_from, date_to, statusFilter, paymentFilter, agentFilter, areaFilter, sortBy, sortOrder, debouncedSearch]);

    // Reload everything when date range changes
    useEffect(() => {
        setPage(0);
        loadSummary();
        loadOrders(0, false);
    }, [date_from, date_to]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reload summary + list when area/agent filters change
    useEffect(() => {
        setPage(0);
        loadSummary();
        loadOrders(0, false);
    }, [agentFilter, areaFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reload list-only when other filters/sort/search change
    useEffect(() => {
        setPage(0);
        loadOrders(0, false);
    }, [statusFilter, paymentFilter, sortBy, sortOrder, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadMore = () => {
        const next = page + 1;
        setPage(next);
        loadOrders(next, true);
    };

    // ── Period navigation ───────────────────────────────────────────────────
    const atLatest = isAtLatest(periodMode, selectedDay, selectedWeekMonday, selectedMonth);

    const handlePrev = () => {
        if (periodMode === 'day') {
            const d = new Date(selectedDay);
            d.setDate(d.getDate() - 1);
            setSelectedDay(toISODate(d));
        } else if (periodMode === 'week') {
            const prev = new Date(selectedWeekMonday);
            prev.setDate(prev.getDate() - 7);
            setSelectedWeekMonday(prev);
        } else {
            setSelectedMonth(sm => {
                if (sm.month === 1) return { year: sm.year - 1, month: 12 };
                return { ...sm, month: sm.month - 1 };
            });
        }
    };

    const handleNext = () => {
        if (atLatest) return;
        if (periodMode === 'day') {
            const d = new Date(selectedDay);
            d.setDate(d.getDate() + 1);
            setSelectedDay(toISODate(d));
        } else if (periodMode === 'week') {
            const next = new Date(selectedWeekMonday);
            next.setDate(next.getDate() + 7);
            setSelectedWeekMonday(next);
        } else {
            setSelectedMonth(sm => {
                if (sm.month === 12) return { year: sm.year + 1, month: 1 };
                return { ...sm, month: sm.month + 1 };
            });
        }
    };

    const handleModeChange = (_, newMode) => {
        if (!newMode) return;
        setPeriodMode(newMode);
        // Reset selections to current period
        const t = new Date();
        setSelectedDay(toISODate(t));
        setSelectedWeekMonday(weekMonday(t));
        setSelectedMonth({ year: t.getFullYear(), month: t.getMonth() + 1 });
    };

    // ── Period label ────────────────────────────────────────────────────────
    const periodLabel = useMemo(() => {
        if (periodMode === 'day') {
            const d = new Date(selectedDay + 'T00:00:00');
            return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
        }
        if (periodMode === 'week') return formatWeekLabel(selectedWeekMonday);
        return `${MONTH_NAMES[selectedMonth.month - 1]} ${selectedMonth.year}`;
    }, [periodMode, selectedDay, selectedWeekMonday, selectedMonth]);

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <Box>
            {/* Period mode tabs */}
            <Tabs
                value={periodMode}
                onChange={handleModeChange}
                variant="fullWidth"
                sx={{ mb: 0, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Month" value="month" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                <Tab label="Week" value="week" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
                <Tab label="Day" value="day" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }} />
            </Tabs>

            {/* Period navigation bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, mb: 1.5 }}>
                <IconButton onClick={handlePrev} size="small"><ChevronLeft /></IconButton>
                <Typography variant="body2" fontWeight="bold">{periodLabel}</Typography>
                <IconButton onClick={handleNext} disabled={atLatest} size="small"><ChevronRight /></IconButton>
            </Box>

            {/* Area breakdown chips */}
            {summary && summary.by_area && summary.by_area.length > 0 && (
                <Box sx={{ mb: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 14, bgcolor: 'primary.main', borderRadius: 2 }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            By Area
                        </Typography>
                        {areaFilter !== '' && (
                            <Typography
                                variant="caption" color="primary.main" sx={{ cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => setAreaFilter('')}>
                                · Clear
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {summary.by_area.map(a => (
                            <Chip
                                key={a.area_id}
                                label={`${a.area_name}  ${a.order_count}`}
                                size="small"
                                clickable
                                onClick={() => setAreaFilter(prev => prev === a.area_id ? '' : a.area_id)}
                                color={areaFilter === a.area_id ? 'primary' : 'default'}
                                variant={areaFilter === a.area_id ? 'filled' : 'outlined'}
                                sx={{ height: 28, fontSize: '0.78rem', fontWeight: areaFilter === a.area_id ? 700 : 400 }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Agent breakdown chips */}
            {summary && summary.by_agent && summary.by_agent.length > 0 && (
                <Box sx={{ mb: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 14, bgcolor: 'secondary.main', borderRadius: 2 }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            By Agent
                        </Typography>
                        {agentFilter !== '' && (
                            <Typography
                                variant="caption" color="secondary.main" sx={{ cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => setAgentFilter('')}>
                                · Clear
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {summary.by_agent.map(a => (
                            <Chip
                                key={a.agent_id ?? 'unassigned'}
                                label={`${a.agent_name}  ${a.order_count}`}
                                size="small"
                                clickable
                                onClick={() => {
                                    const val = a.agent_id ?? 0;
                                    setAgentFilter(prev => prev === val ? '' : val);
                                }}
                                color={agentFilter === (a.agent_id ?? 0) ? 'secondary' : 'default'}
                                variant={agentFilter === (a.agent_id ?? 0) ? 'filled' : 'outlined'}
                                sx={{ height: 28, fontSize: '0.78rem', fontWeight: agentFilter === (a.agent_id ?? 0) ? 700 : 400 }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Summary stats */}
            {summaryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : summary ? (
                <>
                    {/* Stat cards row 1 — order counts */}
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, mb: 0.75,
                        '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                        {[
                            { label: 'Total',     value: summary.total_orders,           labelColor: 'text.secondary',  valueColor: 'text.primary' },
                            { label: 'Delivered', value: summary.delivered_count,         labelColor: 'success.dark',    valueColor: 'success.main' },
                            { label: 'Cancelled', value: summary.cancelled_count,         labelColor: 'error.dark',      valueColor: 'error.main' },
                            { label: 'Due',       value: summary.due_count,               labelColor: 'warning.dark',    valueColor: 'warning.main' },
                            { label: 'Pending',   value: summary.pending_action_count,    labelColor: 'warning.dark',    valueColor: summary.pending_action_count > 0 ? 'warning.main' : 'text.primary' },
                        ].map(({ label, value, labelColor, valueColor }) => (
                            <Card key={label} variant="outlined" sx={{ minWidth: 70, flexShrink: 0 }}>
                                <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
                                    <Typography variant="caption" color={labelColor} display="block">{label}</Typography>
                                    <Typography variant="body2" fontWeight="bold" color={valueColor}>{value}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>

                    {/* Stat cards row 2 — money & customer counts */}
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, mb: 1.5,
                        '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                        {[
                            { label: 'Revenue',   value: inr(summary.total_revenue),     labelColor: 'success.dark',  valueColor: 'success.main' },
                            { label: 'Avg Order', value: inr(summary.avg_order_value),    labelColor: 'text.secondary', valueColor: 'text.primary' },
                            { label: 'Customers', value: summary.unique_customer_count,   labelColor: 'primary.dark',  valueColor: 'primary.main' },
                            { label: 'Walk-ins',  value: summary.walkin_count,            labelColor: 'text.secondary', valueColor: 'text.primary' },
                        ].map(({ label, value, labelColor, valueColor }) => (
                            <Card key={label} variant="outlined" sx={{ minWidth: 80, flexShrink: 0 }}>
                                <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
                                    <Typography variant="caption" color={labelColor} display="block">{label}</Typography>
                                    <Typography variant="body2" fontWeight="bold" color={valueColor}>{value}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </>
            ) : null}

            {/* Product preparation panel */}
            {summary && summary.product_totals && summary.product_totals.length > 0 && (() => {
                const pending = summary.product_totals.filter(p => p.pending_qty > 0);
                const maxQty  = Math.max(...summary.product_totals.map(p => p.total_qty), 1);
                return (
                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ width: 3, height: 16, bgcolor: 'warning.main', borderRadius: 2 }} />
                            <Typography variant="caption" fontWeight={700} color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                What to Prepare
                            </Typography>
                            {pending.length > 0 && (
                                <Typography variant="caption" color="warning.dark" fontWeight={600}>
                                    · {summary.by_status.created + summary.by_status.accepted} active orders
                                </Typography>
                            )}
                        </Box>

                        <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            {summary.product_totals.map((p, idx) => {
                                const pendingPct   = (p.pending_qty / maxQty) * 100;
                                const deliveredPct = (p.delivered_qty / maxQty) * 100;
                                const allDone = p.pending_qty === 0 && p.delivered_qty > 0;
                                return (
                                    <Box key={p.product_id}>
                                        {idx > 0 && <Divider />}
                                        <Box sx={{ px: 1.5, py: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight={600}
                                                    sx={{ color: allDone ? 'text.disabled' : 'text.primary' }}>
                                                    {p.product_name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                                    {p.pending_qty > 0 && (
                                                        <Typography variant="caption" fontWeight={700} color="warning.dark">
                                                            {p.pending_qty % 1 === 0 ? p.pending_qty : p.pending_qty.toFixed(1)} to go
                                                        </Typography>
                                                    )}
                                                    {p.delivered_qty > 0 && (
                                                        <Typography variant="caption" color="success.main" fontWeight={600}>
                                                            {p.delivered_qty % 1 === 0 ? p.delivered_qty : p.delivered_qty.toFixed(1)} ✓
                                                        </Typography>
                                                    )}
                                                    {allDone && (
                                                        <Typography variant="caption" color="success.main" fontWeight={700}>All done</Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            {/* Stacked progress bar */}
                                            <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.100', overflow: 'hidden', display: 'flex' }}>
                                                <Box sx={{ width: `${deliveredPct}%`, bgcolor: 'success.light', transition: 'width 0.3s' }} />
                                                <Box sx={{ width: `${pendingPct}%`, bgcolor: p.pending_qty > 0 ? 'warning.light' : 'transparent', transition: 'width 0.3s' }} />
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                            {/* Legend */}
                            <Box sx={{ display: 'flex', gap: 2, px: 1.5, py: 0.75, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: 'warning.light' }} />
                                    <Typography variant="caption" color="text.secondary">Pending</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: 'success.light' }} />
                                    <Typography variant="caption" color="text.secondary">Delivered</Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Box>
                );
            })()}

            <Divider sx={{ mb: 1.5 }} />

            {/* Search + Sort row */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                    size="small"
                    placeholder="Search by name, phone, order #, product…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: '0 0 66%' }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <FormControl size="small" sx={{ flex: '0 0 calc(34% - 8px)' }}>
                    <InputLabel>Sort</InputLabel>
                    <Select value={`${sortBy}:${sortOrder}`} label="Sort" onChange={(e) => {
                        const [sb, so] = e.target.value.split(':');
                        setSortBy(sb);
                        setSortOrder(so);
                    }}>
                        <MenuItem value="inserted_at:desc">Newest</MenuItem>
                        <MenuItem value="inserted_at:asc">Oldest</MenuItem>
                        <MenuItem value="total_amount:desc">High→Low</MenuItem>
                        <MenuItem value="total_amount:asc">Low→High</MenuItem>
                        <MenuItem value="delivery_date:asc">Delivery ↑</MenuItem>
                        <MenuItem value="delivery_date:desc">Delivery ↓</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Order list */}
            {ordersLoading && page === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : orders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No orders found for this period</Typography>
                </Box>
            ) : (
                <>
                    {orders.map((order) => {
                        const sm = STATUS_META[order.f_status] || { label: order.f_status, color: 'default' };
                        const pm = PAYMENT_META[order.f_payment_status] || { label: order.f_payment_status, color: 'default' };
                        const itemsLabel = (order.items || [])
                            .map((it) => `${it.product_name} ×${Number(it.f_quantity)}`)
                            .join(', ');
                        const dateStr = new Date(order.f_inserted_at).toLocaleString('en-IN', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                        });
                        return (
                            <Card
                                key={order.f_id}
                                variant="outlined"
                                sx={{ mb: 0.75, cursor: 'pointer' }}
                                onClick={() => setDetailOrder(order)}
                            >
                                <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                                <Typography variant="body2" fontWeight="bold" noWrap>
                                                    {order.customer_name || 'Walk-in'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">#{order.f_order_number ?? order.f_id}</Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                                {itemsLabel || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">{dateStr}</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                                            <Typography variant="body2" fontWeight="bold">
                                                {inr(order.f_total_amount)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', mt: 0.25 }}>
                                                <Chip size="small" label={sm.label} color={sm.color}
                                                    sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                                                <Chip size="small" label={pm.label} color={pm.color} variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}

                    {hasMore && (
                        <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                            <Button variant="outlined" size="small" onClick={loadMore} disabled={ordersLoading}>
                                {ordersLoading ? 'Loading…' : 'Load more'}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {/* Order detail dialog (read-only) */}
            {detailOrder && (
                <OrderDetailDialog order={detailOrder} onClose={() => setDetailOrder(null)} />
            )}
        </Box>
    );
}

// ── Read-only order detail dialog ──────────────────────────────────────────

function OrderDetailDialog({ order, onClose }) {
    const sm = STATUS_META[order.f_status] || { label: order.f_status, color: 'default' };
    const pm = PAYMENT_META[order.f_payment_status] || { label: order.f_payment_status, color: 'default' };

    const dateStr = new Date(order.f_inserted_at).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Order #{order.f_order_number ?? order.f_id}</Typography>
                    <Typography variant="caption">{dateStr}</Typography>
                </Box>
                <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
                {/* Customer */}
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                    {order.customer_name || 'Walk-in'}
                    {order.customer_mobile && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            {order.customer_mobile}
                        </Typography>
                    )}
                </Typography>

                {/* Status chips */}
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                    <Chip size="small" label={sm.label} color={sm.color} />
                    <Chip size="small" label={pm.label} color={pm.color} variant="outlined" />
                </Box>

                {/* Items */}
                {(order.items || []).map((item) => (
                    <Box key={item.f_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="body2" color="text.secondary">
                            {item.product_name} × {Number(item.f_quantity)}
                        </Typography>
                        <Typography variant="body2">
                            ₹{Number(item.f_line_total).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                ))}

                <Divider sx={{ my: 1 }} />

                {Number(order.f_discount_amount) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="body2" color="text.secondary">Discount</Typography>
                        <Typography variant="body2" color="warning.main">
                            − ₹{Number(order.f_discount_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography variant="body2" fontWeight="bold">Total</Typography>
                    <Typography variant="body2" fontWeight="bold">
                        ₹{Number(order.f_total_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </Typography>
                </Box>

                {/* Extra info */}
                {order.f_delivery_date && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Delivery: {new Date(order.f_delivery_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                )}
                {order.agent_name && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        Agent: {order.agent_name}
                    </Typography>
                )}
                {order.f_notes && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {order.f_notes}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1 }}>
                <Button onClick={onClose} variant="text" size="small" color="inherit">Close</Button>
            </DialogActions>
        </Dialog>
    );
}
